import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GroupRepresentation, KeycloakAdminApi } from '../../services/keycloak-admin-api';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { SidebarService } from '../../services/sidebar.service';

interface BreadcrumbItem {
  id: string;
  name: string;
  path: string; // This will store the cumulative URL path, not the group path
}

@Component({
  selector: 'app-groups-list',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './groups-list.html',
  styleUrl: './groups-list.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GroupsList implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  protected sidebarService = inject(SidebarService);

  currentGroups: GroupRepresentation[] = [];
  filteredGroups: GroupRepresentation[] = [];
  isLoading = false;
  errorMessage: string | null = null;
  searchControl = new FormControl('');

  // Navigation state
  currentParentId: string | null = null;
  breadcrumbs: BreadcrumbItem[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalGroups = 0;
  hasMorePages = false;

  ngOnInit() {
    // Initialize from URL query parameters
    this.initializeFromUrl();

    // Listen to route changes for browser back/forward navigation
    this.route.queryParams.subscribe(() => {
      this.initializeFromUrl();
    });

    // Set up search with debounce
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 1; // Reset to first page when searching
        this.updateUrlWithCurrentState(); // Update URL with new page
        this.performSearch();
      });
  }

  private async initializeFromUrl() {
    const pathParam = this.route.snapshot.queryParams['path'];
    const pageParam = this.route.snapshot.queryParams['page'];

    if (pathParam) {
      // Parse the hierarchical path (e.g., "parent-id,child-id,grandchild-id")
      const groupIds = pathParam.split(',').filter((id: string) => id);
      await this.buildBreadcrumbsFromPath(groupIds);
    } else {
      // Load root groups
      this.breadcrumbs = [];
      this.currentParentId = null;
    }

    // Set the current page from URL or default to 1
    this.currentPage = pageParam ? parseInt(pageParam, 10) : 1;

    this.loadGroups(this.currentParentId || undefined);
  }

  private async buildBreadcrumbsFromPath(groupIds: string[]) {
    this.breadcrumbs = [];

    for (let i = 0; i < groupIds.length; i++) {
      const groupId = groupIds[i];
      // Build the cumulative path for this breadcrumb
      const cumulativePath = groupIds.slice(0, i + 1).join(',');

      try {
        const group = await this.keycloakApi.getGroupById(groupId);
        if (group) {
          this.breadcrumbs.push({
            id: group.id!,
            name: group.name || 'Unknown',
            path: cumulativePath,
          });
        }
      } catch (error) {
        console.error(`Failed to load group ${groupId}:`, error);
        // If we can't load a group in the path, redirect to root
        this.router.navigate(['/groups']);
        return;
      }
    }

    // Set current parent to the last group in the path
    this.currentParentId = groupIds[groupIds.length - 1];
  }

  loadGroups(parentId?: string) {
    this.isLoading = true;
    this.errorMessage = null;
    this.currentParentId = parentId || null;

    const promise = parentId ? this.loadSubGroups(parentId) : this.loadTopLevelGroups();

    promise
      .then((groups) => {
        this.currentGroups = groups;
        // this.filterGroups();
        this.isLoading = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load groups: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  private async loadTopLevelGroups(): Promise<GroupRepresentation[]> {
    const searchTerm = this.searchControl.value?.trim();
    const first = (this.currentPage - 1) * this.pageSize;

    // Only pass search term if it's not empty to avoid 500 errors
    const searchParam = searchTerm && searchTerm.length > 0 ? searchTerm : undefined;

    this.totalGroups = (await this.keycloakApi.getGroupsCount(searchParam, true))?.count || 0;

    return this.keycloakApi.getGroups(searchParam, first, this.pageSize).then((groups) => {
      // Check if there are more pages based on returned group count
      this.hasMorePages = groups.length === this.pageSize;
      // Don't estimate total - we don't have accurate count from the API
      // this.totalGroups = 0; // Reset to 0 to indicate unknown total
      return groups;
    });
  }

  private async loadSubGroups(parentId: string): Promise<GroupRepresentation[]> {
    const searchTerm = this.searchControl.value?.trim();
    const first = (this.currentPage - 1) * this.pageSize;

    // Only pass search term if it's not empty to avoid 500 errors
    const searchParam = searchTerm && searchTerm.length > 0 ? searchTerm : undefined;

    this.keycloakApi.getGroupById(parentId).then((group) => {
      if (group) {
        this.totalGroups = group.subGroupCount || 0;
      }
    });

    // this.totalGroups = (await this.keycloakApi.getGroupsCount(searchParam, true))?.count || 0;

    return this.keycloakApi
      .getGroupChildren(parentId, searchParam, first, this.pageSize)
      .then((groups) => {
        // Check if there are more pages based on returned group count
        this.hasMorePages = groups.length === this.pageSize;
        // Don't estimate total - we don't have accurate count from the API
        // this.totalGroups = 0; // Reset to 0 to indicate unknown total
        return groups;
      });
  }

  filterGroups(): void {
    const searchTerm = this.searchControl.value?.toLowerCase() || '';

    if (!searchTerm.trim()) {
      this.filteredGroups = [...this.currentGroups];
      return;
    }

    // Filter current groups by name or path
    this.filteredGroups = this.currentGroups.filter(
      (group) =>
        group.name?.toLowerCase().includes(searchTerm) ||
        group.path?.toLowerCase().includes(searchTerm)
    );
  }

  performSearch() {
    // Always reload with search, whether at top level or in subgroups
    this.loadGroups(this.currentParentId || undefined);
  }

  // Navigation methods
  navigateToGroup(group: GroupRepresentation): void {
    if (!group.id || !this.hasSubgroups(group)) return;

    // Build the new query parameter path
    const currentPath = this.buildCurrentQueryPath();
    const newPath = currentPath ? `${currentPath},${group.id}` : group.id;

    // Navigate to the new URL with query parameters, reset to page 1 for the new level
    this.router.navigate(['/groups'], {
      queryParams: {
        path: newPath,
        page: 1,
      },
    });
  }

  navigateToBreadcrumb(index: number): void {
    if (index === -1) {
      // Navigate to root
      this.navigateToRoot();
      return;
    }

    // Build path up to the specified breadcrumb
    const pathSegments = this.breadcrumbs.slice(0, index + 1).map((b) => b.id);
    const newPath = pathSegments.join(',');

    // Navigate back to the breadcrumb level, preserving any stored page for that level
    // For now, we'll reset to page 1, but this could be enhanced to remember page per level
    this.router.navigate(['/groups'], {
      queryParams: {
        path: newPath,
        page: 1,
      },
    });
  }

  navigateToRoot(): void {
    this.router.navigate(['/groups'], { queryParams: { page: 1 } });
  }

  private buildCurrentQueryPath(): string {
    return this.breadcrumbs.map((b) => b.id).join(',');
  }

  // Pagination methods
  goToPage(event: Event): void {
    const customEvent = event as CustomEvent;
    console.log(customEvent);
    const page = customEvent?.detail?.currentPage ?? 1;
    if (page < 0) return;

    this.currentPage = page;
    this.updateUrlWithCurrentState();
    this.loadGroups(this.currentParentId || undefined);
  }

  private navigateToPageNumber(page: number): void {
    if (page < 0) return;
    this.currentPage = page;
    this.updateUrlWithCurrentState();
    this.loadGroups(this.currentParentId || undefined);
  }

  private updateUrlWithCurrentState(): void {
    const queryParams: Record<string, string | number> = { page: this.currentPage };

    if (this.breadcrumbs.length > 0) {
      queryParams['path'] = this.buildCurrentQueryPath();
    }

    this.router.navigate(['/groups'], {
      queryParams,
      replaceUrl: true, // Use replaceUrl to avoid cluttering browser history with page changes
    });
  }

  nextPage(): void {
    if (this.hasMorePages) {
      this.navigateToPageNumber(this.currentPage + 1);
    }
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.navigateToPageNumber(this.currentPage - 1);
    }
  }

  getTotalPages(): number {
    if (this.totalGroups <= 0) return 0; // Unknown total
    return Math.ceil(this.totalGroups / this.pageSize);
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const pages: number[] = [];

    // Show up to 5 page numbers around current page
    const start = Math.max(0, this.currentPage - 2);
    const end = Math.min(totalPages, start + 5);

    for (let i = start; i < end; i++) {
      pages.push(i);
    }

    return pages;
  }

  // Utility methods
  hasSubgroups(group: GroupRepresentation): boolean {
    return (
      !!(group.subGroups && group.subGroups.length > 0) ||
      !!(group.subGroupCount && group.subGroupCount > 0)
    );
  }

  getSubgroupText(group: GroupRepresentation): string {
    if (group.subGroupCount && group.subGroupCount > 0) {
      return `${group.subGroupCount} subgroup${group.subGroupCount === 1 ? '' : 's'}`;
    }
    return '';
  }

  refreshGroups(): void {
    this.currentPage = 1;
    this.loadGroups(this.currentParentId || undefined);
  }

  getCurrentLocationText(): string {
    if (this.breadcrumbs.length === 0) {
      return 'All Groups';
    }
    return `Groups in "${this.breadcrumbs[this.breadcrumbs.length - 1].name}"`;
  }

  // Sidebar functionality
  openGroupEdit(group: GroupRepresentation, event: Event) {
    event.stopPropagation();
    if (!group.id) return;

    this.sidebarService.openGroupEdit(group.id, group);
  }

  clearSearch() {
    this.searchControl.setValue('');
  }
}
